import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const VerifyPending = ({ onSuccess }) => {
  const email = localStorage.getItem('pendingVerificationEmail');
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [cooldown, setCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  // Poll every 5 seconds to check if verified
  useEffect(() => {
    if (!email) return;

    const poll = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/check-verified?email=${encodeURIComponent(email)}`
        );
        if (res.data.verified) {
          clearInterval(pollRef.current);
          // Store token and user so App.js picks them up
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('user', JSON.stringify(res.data.user));
          localStorage.removeItem('pendingVerificationEmail');
          if (onSuccess) onSuccess(res.data.user, res.data.token);
          setVerified(true);
          // Small delay so user sees the success state before redirect
          setTimeout(() => navigate('/'), 2000);
        }
      } catch (err) {
        // Silently ignore poll errors — don't interrupt the user
      }
    };

    // Poll immediately then every 5s
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [email]); // eslint-disable-line

  // Cooldown timer for resend button
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || cooldown > 0) return;
    setStatus('sending');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/resend-verification`, { email });
      setStatus('sent');
      setCooldown(60);
    } catch (err) {
      setStatus('error');
    }
  };

  // Manual "I've already verified" check
  const handleManualCheck = async () => {
    if (!email) return;
    setChecking(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/auth/check-verified?email=${encodeURIComponent(email)}`
      );
      if (res.data.verified) {
        clearInterval(pollRef.current);
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.removeItem('pendingVerificationEmail');
        if (onSuccess) onSuccess(res.data.user, res.data.token);
        setVerified(true);
        setTimeout(() => navigate('/'), 2000);
      } else {
        setStatus('not_verified');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch (err) {
      setStatus('error');
    }
    setChecking(false);
  };

  // Verified state — show success before redirect
  if (verified) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0a0a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Jost, sans-serif', padding: '24px',
      }}>
        <div style={{ width: '100%', maxWidth: '480px', textAlign: 'center' }}>
          <div style={{
            width: '72px', height: '72px',
            border: '1px solid #4a7a4a', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 32px', fontSize: '1.8rem',
          }}>✓</div>
          <p style={{ fontSize: '0.65rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px' }}>
            Flakon
          </p>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', lineHeight: 1.2 }}>
            Email verified
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#7aad7a', lineHeight: 1.8 }}>
            Taking you into Flakon...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0a0a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Jost, sans-serif', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px', textAlign: 'center' }}>

        {/* Icon */}
        <div style={{
          width: '72px', height: '72px', border: '1px solid #2a2a2a', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 32px', fontSize: '1.8rem',
        }}>✉</div>

        <p style={{ fontSize: '0.65rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px' }}>
          Flakon
        </p>

        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', lineHeight: 1.2 }}>
          Check your inbox
        </h1>

        <p style={{ fontSize: '0.9rem', color: '#6b6460', lineHeight: 1.8, marginBottom: '8px' }}>
          We've sent a verification link to
        </p>

        {email && (
          <p style={{ fontSize: '0.9rem', color: '#c9a96e', marginBottom: '32px', letterSpacing: '0.05em' }}>
            {email}
          </p>
        )}

        <p style={{ fontSize: '0.85rem', color: '#4a4440', lineHeight: 1.8, marginBottom: '40px' }}>
          Click the link in the email to verify your account. This page will automatically update once you've verified — even if you open the email on another device.
        </p>

        {/* Polling indicator */}
        <p style={{ fontSize: '0.65rem', color: '#2a2a2a', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '32px' }}>
          ● Waiting for verification...
        </p>

        {/* Manual check button */}
        <div style={{ marginBottom: '16px' }}>
          {status === 'not_verified' && (
            <p style={{ color: '#e07070', fontSize: '0.85rem', marginBottom: '12px' }}>
              Not verified yet — check your email and click the link.
            </p>
          )}
          <button
            onClick={handleManualCheck}
            disabled={checking}
            style={{
              background: '#c9a96e', border: 'none',
              padding: '14px 40px', color: '#0a0a0a',
              fontFamily: 'Jost, sans-serif', fontSize: '0.75rem',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              cursor: checking ? 'default' : 'pointer',
              width: '100%', maxWidth: '320px',
              opacity: checking ? 0.6 : 1,
            }}
          >
            {checking ? 'Checking...' : "I've already verified →"}
          </button>
        </div>

        {/* Resend button */}
        <div style={{ marginBottom: '24px' }}>
          {status === 'sent' && (
            <p style={{ color: '#7aad7a', fontSize: '0.85rem', marginBottom: '8px' }}>Email resent! Check your inbox.</p>
          )}
          {status === 'error' && (
            <p style={{ color: '#e07070', fontSize: '0.85rem', marginBottom: '8px' }}>Something went wrong. Please try again.</p>
          )}
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || status === 'sending'}
            style={{
              background: 'none', border: '1px solid #2a2a2a',
              padding: '14px 40px',
              color: cooldown > 0 ? '#3a3a3a' : '#a09890',
              fontFamily: 'Jost, sans-serif', fontSize: '0.75rem',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              cursor: cooldown > 0 ? 'default' : 'pointer',
              width: '100%', maxWidth: '320px',
            }}
          >
            {status === 'sending' ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : "Didn't receive it? Resend"}
          </button>
        </div>

        <p style={{ fontSize: '0.75rem', color: '#2a2a2a', lineHeight: 1.7 }}>
          Make sure to check your spam folder.<br />
          The link expires after 1 hour.
        </p>

        <a href="/" style={{ display: 'block', marginTop: '32px', fontSize: '0.72rem', color: '#3a3a3a', textDecoration: 'none', letterSpacing: '0.05em' }}>
          ← Back to Flakon
        </a>
      </div>
    </div>
  );
};

export default VerifyPending;